import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { submitQuote } from "@/lib/api";

const QuoteDialog = ({ open, onOpenChange, preset }) => {
  const [form, setForm] = useState({ client_name: "", email: "", phone: "", vehicle: "", notes: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitQuote({
        client_name: form.client_name,
        email: form.email,
        phone: form.phone,
        source: preset?.source || "contact",
        service: preset?.service || "",
        vehicle_info: {
          vehicle_type: preset?.vehicle_type || "",
          year_make_model: form.vehicle,
        },
        wrap_specifications: preset?.wrap_specifications || null,
        custom_decal_specs: preset?.custom_decal_specs || null,
        build_image: preset?.buildImage || null,
        notes: preset?.notePrefix ? preset.notePrefix + (form.notes ? "\n" + form.notes : "") : form.notes,
      });
      toast.success("Quote request received", {
        description: "The G5 crew will reach out within one business day.",
      });
      setForm({ client_name: "", email: "", phone: "", vehicle: "", notes: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not send your request", { description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quote-dialog" className="bg-gunmetal border-edge text-slate-100 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl uppercase tracking-tight">
            Lock In This Build
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Send your configuration straight to the G5 studio. We reply with a written quote and install window.
          </DialogDescription>
        </DialogHeader>

        {preset?.buildImage && (
          <img
            data-testid="quote-build-image-preview"
            src={preset.buildImage}
            alt="Your wrap build"
            className="rounded-lg border border-edge w-full"
          />
        )}

        {preset?.summary && (
          <div data-testid="quote-config-summary" className="rounded-lg border border-edge bg-panel p-3 font-mono text-xs leading-relaxed text-slate-300 whitespace-pre-line">
            {preset.summary}
          </div>
        )}

        <form data-testid="quote-request-form" onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              data-testid="quote-name-input"
              required
              placeholder="Full name"
              value={form.client_name}
              onChange={set("client_name")}
              className="bg-panel border-edge"
            />
            <Input
              data-testid="quote-phone-input"
              placeholder="Phone"
              value={form.phone}
              onChange={set("phone")}
              className="bg-panel border-edge"
            />
          </div>
          <Input
            data-testid="quote-email-input"
            required
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={set("email")}
            className="bg-panel border-edge"
          />
          <Input
            data-testid="quote-vehicle-input"
            placeholder="Vehicle year, make & model (e.g. 2023 BMW M4)"
            value={form.vehicle}
            onChange={set("vehicle")}
            className="bg-panel border-edge"
          />
          <Textarea
            data-testid="quote-notes-input"
            placeholder="Anything else we should know? Timeline, references, ideas..."
            value={form.notes}
            onChange={set("notes")}
            className="bg-panel border-edge min-h-[90px]"
          />
          <Button
            data-testid="quote-form-submit-btn"
            type="submit"
            disabled={sending}
            className="w-full bg-velocity hover:bg-velocitydark text-white font-semibold"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? "Sending..." : "Request Quote With This Build"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDialog;
